import { useCallback, useEffect, useState, type ReactNode } from "react"
import type { Me } from "@shared/api"
import { useAuth } from "../context/AuthContext"
import { ApiError, api } from "../services/api"
import { LoadingScreen, NoAccess, SetPassword, SignIn } from "./AuthScreens"
import { ErrorState } from "./ui"

/**
 * Portado del tablero de reuniones (web/src/components/AuthGate.tsx):
 *
 *   loading      → "Cargando…"
 *   recoveryMode → <SetPassword/>
 *   !user        → <SignIn/>
 *   si no        → <AccessGate>{children}</AccessGate>
 */
export function AuthGate({ children }: { children: (me: Me) => ReactNode }) {
  const { user, loading, recoveryMode } = useAuth()

  if (loading) return <LoadingScreen />
  if (recoveryMode) return <SetPassword />
  if (!user) return <SignIn />
  return <AccessGate>{children}</AccessGate>
}

/**
 * La respuesta a "un usuario logueado sin permiso no debe ver un tablero en blanco".
 *
 * GATEA CON `me.canViewDashboard`, LA RESPUESTA DEL SERVIDOR (GET /api/dashboard/me),
 * no con el claim del JWT ni con `me.isAdmin`. Los dos matices:
 *
 *  · el claim (`claimCanView`) solo sirve para renderizar optimistamente y evitar un
 *    flash. UNA SOLA fuente de verdad es el servidor, así la UI nunca puede
 *    contradecir los 403 de la API;
 *  · `me.isAdmin` es informativo y PUEDE VENIR FALSE en un 200 — es exactamente el
 *    caso de una cuenta de HR. Gatear con él dejaría afuera justo a quien este
 *    permiso vino a incluir.
 *
 * Y un fallo de /me que NO sea 401/403 muestra <ErrorState/>, no <NoAccess/>: no hay
 * que confundir "el servidor está caído" con "no tenés permiso". Ese es exactamente el
 * caso del 503 que devuelve el middleware cuando Supabase Auth no responde.
 */
function AccessGate({ children }: { children: (me: Me) => ReactNode }) {
  const { user, refreshSession } = useAuth()
  const [me, setMe] = useState<Me | null>(null)
  const [forbidden, setForbidden] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [retrying, setRetrying] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setError(null)
    api
      .me(controller.signal)
      .then((result) => {
        setMe(result)
        setForbidden(!result.canViewDashboard)
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true)
          return
        }
        setError(err instanceof Error ? err : new Error(String(err)))
      })
    return () => controller.abort()
  }, [user?.id, attempt])

  const retry = useCallback(async () => {
    setRetrying(true)
    try {
      // Renovar el token es lo que trae el app_metadata nuevo (el rol o el flag):
      // sin esto, un permiso recién otorgado no se ve hasta el próximo login.
      await refreshSession()
    } catch {
      // Si el refresh falla se reintenta /me igual: puede haber sido un hipo de red.
    } finally {
      setForbidden(false)
      setAttempt((a) => a + 1)
      setRetrying(false)
    }
  }, [refreshSession])

  if (error) {
    return (
      <div className="auth-page">
        <div className="auth-card">
          <ErrorState
            title="No se pudo verificar tu sesión"
            message={error.message}
            onRetry={() => setAttempt((a) => a + 1)}
          />
        </div>
      </div>
    )
  }

  if (forbidden) {
    return <NoAccess email={me?.email ?? user?.email ?? null} onRetry={retry} retrying={retrying} />
  }

  if (!me) return <LoadingScreen text="Verificando permisos…" />

  return <>{children(me)}</>
}
